import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { TitleService } from './services/title/title.service';
import { SignOutService } from './services/sign-out-state/sign-out-state.service';
import { Router, RouterOutlet } from '@angular/router';
import { Bind } from 'primeng/bind';
import { Button } from 'primeng/button';
import { Dialog } from 'primeng/dialog';
import { Toast } from 'primeng/toast';
import { filter, fromEvent, interval, merge, throttleTime } from 'rxjs';

const IDLE_LIMIT_MS = 45 * 60 * 1000;
// TODO: Toggle on for QA Testing. Sets the timeout to 2m 10s. Can be removed after QA testing.
// const IDLE_LIMIT_MS = 130000;
const WARNING_MS = 2 * 60 * 1000;
export const LAST_ACTIVITY_STORAGE_KEY = 'lastActivity';
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [RouterOutlet, Bind, Toast, Dialog, Button, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AppComponent {
  private translateService = inject(TranslateService);
  private titleService = inject(TitleService);
  private router = inject(Router);
  private signOutService = inject(SignOutService);

  private msRemaining = signal(IDLE_LIMIT_MS);
  protected showTimeoutWarning = computed(() => this.msRemaining() <= WARNING_MS);
  protected timeRemaining = computed(() => {
    const seconds = Math.max(0, Math.ceil(this.msRemaining() / 1000));
    return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
  });

  constructor() {
    const userLang = navigator.language;
    const languageCode = userLang.split('-')[0];
    this.translateService.setDefaultLang(languageCode);
    this.translateService.use(languageCode);

    this.stayLoggedIn();

    merge(...ACTIVITY_EVENTS.map(name => fromEvent(document, name)))
      .pipe(
        filter(() => !this.showTimeoutWarning()),
        throttleTime(1000),
        takeUntilDestroyed()
      )
      .subscribe(() => this.stayLoggedIn());

    interval(1000)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.checkIdle());
  }

  protected stayLoggedIn(): void {
    localStorage.setItem(LAST_ACTIVITY_STORAGE_KEY, Date.now().toString());
    this.msRemaining.set(IDLE_LIMIT_MS);
  }

  protected logOut(): void {
    this.signOutService.signOut();
  }

  private checkIdle(): void {
    if (this.router.url.startsWith('/signedout')) {
      return;
    }
    const lastActivity = Number(localStorage.getItem(LAST_ACTIVITY_STORAGE_KEY)) || Date.now();
    this.msRemaining.set(lastActivity + IDLE_LIMIT_MS - Date.now());
    if (this.msRemaining() <= 0) {
      this.logOut();
    }
  }
}
